import React, { useState } from 'react';
import CurrencyInput from '../currency-input';
import formatCurrency from '../../utils/format-currency';
import { z } from "zod";

function Loading() {
  return (
    <div className="fixed w-full h-full top-0 left-0 flex justify-center items-center bg-black/50 z-[999]">
      <div className="py-6 px-10 bg-white rounded-md text-lg font-bold">
        Please wait...
      </div>
    </div>
  )
}

function OtherField({ value, onChange, error }) {
  return (
    <div className="mb-5">
      <div className="relative">
        <span className="absolute left-3 top-2 text-2xl">$</span>

        <CurrencyInput
          name="other_value"
          id="other_value"
          maxLength="10"
          value={value}
          onChange={onChange}
          className="pl-8"
        />
      </div>

      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}
    </div>
  )
}

const SubscriptionUpgrade = ({
  csrfToken,
  currentTotal,
  defaultOtherValue,
  defaultSelected,
  donorFirstName,
  upgradeValues,
  url,
}) => {
  const [isPending, setIsPending] = useState(false);
  const [selected, setSelected] = useState(defaultSelected);
  const [otherValue, setOtherValue] = useState(defaultOtherValue);
  const [otherError, setOtherError] = useState(null);
  const [newTotal, setNewTotal] = useState(() => calculateNewTotal(defaultSelected));

  const format = '$0,0[.]00';

  const schema = z.object({
    otherValue: z.coerce.number().gt(0, { message: "Please enter a valid amount." })
  });

  function calculateNewTotal(newSelected) {
    return newSelected === 'other' ?
      calculateTotalWithOther(otherValue) :
      upgradeValues[newSelected].total
  }

  function calculateTotalWithOther(value) {
    if (value === '' || value === undefined) {
      value = 0;
    }

    return (parseFloat(value) + parseFloat(currentTotal)).toFixed(2)
  }

  function changeOtherValue(event) {
    const otherValue = event.target.value;
    const newTotal = calculateTotalWithOther(otherValue);

    setNewTotal(newTotal);
    setOtherValue(otherValue);
  }

  function changeSelection (event) {
    const newSelected = event.target.value;

    setSelected(newSelected);
    setNewTotal(calculateNewTotal(newSelected));
  }

  function formSubmit(event) {
    event.preventDefault();

    const valid = validate();

    if (valid) {
      setIsPending(true);
      return event.target.submit();
    }
  }

  function validate() {
    if (selected != 'other') {
      return true;
    }

    const validationResult = schema.safeParse({ otherValue })

    if (!validationResult.success) {
      setOtherError(validationResult.error.flatten().fieldErrors.otherValue);
      return false;
    }

    setOtherError(null);
    return true;
  }

  return (
    <div>
      <p className="mb-5">
        Hey {donorFirstName}, your support is making a difference—thank you.
      </p>

      <p className="mb-5">
        Ready to grow your impact even more?
      </p>

      <p className="mb-5">
        Increase your current <strong>{formatCurrency(currentTotal * 100, format)}/month</strong> donation by:
      </p>

      <form action={url} method="POST" onSubmit={formSubmit}>
        <input type="hidden" name="authenticity_token" value={csrfToken} />

        <div className="mb-4 gap-5 donation-levels grid grid-cols-2 md:flex">
          {Object.keys(upgradeValues).map((key) =>
            <div key={key}>
              <input
                type="radio"
                value={key}
                name="upgrade_level"
                id={`upgrade_level_${key}`}
                onChange={changeSelection}
                checked={selected === key}
                className="sr-only"
              />

              <label
                htmlFor={`upgrade_level_${key}`}
                className="rounded-lg text-2xl px-6 py-6 border border-solid border-black cursor-pointer whitespace-nowrap"
              >
                + ${upgradeValues[key].increase}
              </label>
            </div>
          )}

          <div>
            <input
              type="radio"
              value="other"
              name="upgrade_level"
              id="upgrade_level_other"
              onChange={changeSelection}
              checked={selected === 'other'}
              className="sr-only"
            />

            <label
              htmlFor="upgrade_level_other"
              className="rounded-lg text-2xl px-6 py-6 border border-solid border-black cursor-pointer whitespace-nowrap"
            >
              + Other
            </label>
          </div>
        </div>

        {selected === 'other' && (
          <OtherField
            value={otherValue}
            onChange={changeOtherValue}
            error={otherError}
          />
        )}

        <div className="relative flex items-center bg-neutral-100 py-5 pl-14 pr-6 mb-6 rounded-lg text-lg font-medium">
          <svg className="icon-heart w-[20px] h-[20px] absolute top-6 left-6">
            <use xlinkHref="#icon-heart" />
          </svg>

          <p>Future donations will be <strong>{formatCurrency(newTotal * 100, format)}/month</strong></p>
        </div>

        <button
          type="submit"
          className="btn w-full md:w-auto disabled:!bg-gray-300 disabled:!border-gray-300 disabled:cursor-wait text-black"
          disabled={isPending}
          id="btn-submit"
        >
          Confirm
        </button>
      </form>

      {isPending && <Loading />}
    </div>
  )
}

export default SubscriptionUpgrade;